/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type PropsWithChildren, useRef, useEffect, useCallback } from 'react';
import { Link } from '@/components/link';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import { useTranslation } from 'react-i18next';
import logo from '/images/brd-logo.svg';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import type { SearchContentData } from '@/lib/api/search-content.server';
import { HeaderPanelProvider } from './header-panel-provider';
import { SearchContentBoundary, LocationKeyedSearch } from './search-content-boundary';
import UiTargetSmokeTest_GenericMarker from '@/extensions/ui-target-smoke-test/components/generic-marker';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';
@Component('header', {
  name: 'Header',
  group: 'Layout',
  description: 'Global site header with navigation, search, and cart',
  embedded: true,
  component_id: 'header'
})
@RegionDefinition([{
  id: 'announcement',
  name: 'Announcement'
}])
export class HeaderMetadata {}
export interface HeaderProps extends PropsWithChildren {
  beforeHeader?: ReactNode;
  /**
   * Slot rendered above the header's main row. Used for announcement banners
   * or other above-the-fold content that should sit at the top of the page.
   */
  announcementSlot?: ReactNode;
  variant?: 'full' | 'checkout';
  desktopNavigation?: ReactNode;
  mobileNavigationToggle?: ReactNode;
  mobileNavigationMenu?: ReactNode;
  /** Promise resolving to merchant-managed default search suggestion content. */
  searchContent?: Promise<SearchContentData>;
}
export default function Header({
  children,
  beforeHeader,
  variant = 'full',
  desktopNavigation = children,
  mobileNavigationToggle,
  mobileNavigationMenu,
  searchContent
}: HeaderProps): ReactElement {
  const {
    t
  } = useTranslation('header');
  const headerRef = useRef<HTMLElement>(null);
  const config = useConfig();
  const showChat = variant === 'full' && (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) && validateShopperAgentConfig(config.commerceAgent);
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      const height = `${headerRef.current.offsetHeight}px`;
      headerRef.current.style.setProperty('--header-height', height);
      document.documentElement.style.setProperty('--header-height', height);
    }
  }, []);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateHeaderHeight]);
  if (variant === 'checkout') {
    return <header ref={headerRef} className="bg-header-background text-header-foreground border-b border-header-border sticky top-[var(--ticker-height)] z-50">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img src={logo} alt={t('logoAlt')} className="h-8 lg:h-10 w-auto [filter:var(--header-logo-filter)]" />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>;
  }
  return <header ref={headerRef} className="bg-header-background text-header-foreground border-b border-header-border sticky top-[var(--ticker-height)] z-50">
            <div className="flex justify-end section-container">{beforeHeader}</div>
            {/*
                Single Search mount with responsive grid placement (no matchMedia / hydration flash).
                Mobile:  row1 logo + icons | row2 flex(hamburger + search)
                Desktop: row1 logo + search + icons | row2 category nav
             */}
            <HeaderPanelProvider>
                <div className="section-container py-3 lg:pt-4 lg:pb-0 lg:px-12">
                    <div className="grid grid-cols-[auto_1fr_auto] gap-x-1 lg:gap-x-6">
                        <Link to="/" className="col-start-1 row-start-1 flex shrink-0 items-center" data-testid="header-logo">
                            <img src={logo} alt={t('logoAlt')} className="h-8 w-auto lg:h-10 [filter:var(--header-logo-filter)]" />
                        </Link>

                        <div className="col-span-3 row-start-2 flex min-w-0 items-center gap-2 self-center pt-3 lg:col-span-1 lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:pt-0" data-testid="header-search">
                            {mobileNavigationToggle ? <div className="shrink-0 lg:hidden">{mobileNavigationToggle}</div> : null}
                            <div className="min-w-0 flex-1">
                                <SearchContentBoundary searchContent={searchContent}>
                                    {categories => <LocationKeyedSearch {...categories} />}
                                </SearchContentBoundary>
                            </div>
                        </div>

                        <div className="col-start-3 row-start-1 flex items-center gap-x-3 self-center lg:gap-x-8">
                            {showChat && <Button variant="ghost" size="icon" className="cursor-pointer text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity" onClick={() => launchChat()} aria-label={t('openChat')}>
                                    <SparklesIcon />
                                </Button>}
                            <UiTargetSmokeTest_GenericMarker />
                            <WishlistIcon />
                            <UserActions />
                            <CartBadge />
                        </div>

                        {desktopNavigation ? <div className="col-span-3 row-start-2 hidden items-center pt-2 lg:flex">
                                {desktopNavigation}
                            </div> : null}
                    </div>
                    {mobileNavigationMenu}
                    <UiTargetSmokeTest_GenericMarker />
                </div>
            </HeaderPanelProvider>
        </header>;
}